import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Input,
  Row,
  Col,
  Menu,
  Table,
  Divider,
  Button,
  Card,
  Typography,
  notification,
  Skeleton
} from "antd";
import { SaveFilled } from "@ant-design/icons";

import { getRoleById, createRole, updateRole, getViews } from "../../../../API/roles";
import { getControlsArea } from "../../../../API/controls";

import WithPermission from "../../../Common/WithPermission/WithPermission";
import BackButton from "../../../Common/BackButton/BackButton";
import BreadCrumb from "../../../Common/BreadCrumb/BreadCrumb";
import PageTitle from "../../../Common/PageTitle/PageTitle";
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";

import { getColumns } from "./components/PermissionsTable/columns";

import { useAuth } from "../../../Context/AuthContext";

import "./RoleView.css";

const { Title } = Typography;

const permissionsView = {
  assets: ["View", "Create", "Update", "Delete"],
  dependencies: ["View", "Create", "Update", "Delete"],
  controls: ["View", "Create", "Update", "Delete"],
  assignment: ["View", "Assign", "Unassign"],
  evaluation: ["View", "Update"],
  gaps: ["View", "Update"],
  tasks: ["View", "Update"],
  workflows: ["View", "Create", "Update", "Delete"],
};

const RoleView = () => {
  const { userData } = useAuth();
  const isSuperuser = userData?.is_superuser === true;

  const [api, contextHolder] = notification.useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [selectedView, setSelectedView] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const isEditing = Boolean(id);
  const [views, setViews] = useState([]);
  const [roleData, setRoleData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const fetchedViews = await getViews();
        const orderedViews = [...fetchedViews].sort((a, b) => a.index - b.index);
        setViews(orderedViews);

        const firstVisibleView = orderedViews.find((view) => view.frontend_view !== "workflows");
        if (firstVisibleView) {
          setSelectedView(firstVisibleView.frontend_view);
        }

        const areas = await getControlsArea();

        if (!isEditing) {
          // Caso: Nuevo rol - crear estructura vacía
          const baseDataSource = areas.map((item) => {
            const permissions = {};
            orderedViews.forEach((view) => {
              const perms = permissionsView[view.frontend_view] || [];
              permissions[view.frontend_view] = {};
              perms.forEach((perm) => {
                permissions[view.frontend_view][perm] = false;
              });
            });

            return {
              key: item.id.toString(),
              id: item.id,
              area: item.name,
              permissions,
            };
          });
          setDataSource(baseDataSource);
        } else {
          // Caso: Editar rol - cargar datos existentes
          const data = await getRoleById(id);
          setRoleData(data);
          form.setFieldsValue({
            name: data.name,
            description: data.description,
          });

          // Crear mapa de permisos para fácil acceso
          const permissionMap = {};
          data.permissions.forEach((p) => {
            const key = `${p.area.id}_${p.view.frontend_view}`;
            
            if (p.view.frontend_view === "assignment") {
              // Mapeo especial para assignment
              permissionMap[key] = {
                View: p.read || false,
                Assign: p.create || false,  // create → Assign
                Unassign: p.update || false, // update → Unassign
                Delete: p.delete || false,
                Create: false,  // No se usa en UI para assignment
                Update: false   // No se usa en UI para assignment
              };
            } else {
              // Mapeo normal para otras vistas
              permissionMap[key] = {
                View: p.read || false,
                Create: p.create || false,
                Update: p.update || false,
                Delete: p.delete || false,
                Assign: false,   // No se usa en otras vistas
                Unassign: false  // No se usa en otras vistas
              };
            }
          });

          // Construir dataSource combinando áreas con permisos
          const combinedDataSource = areas.map((area) => {
            const permissions = {};
            orderedViews.forEach((view) => {
              const perms = permissionsView[view.frontend_view] || [];
              const key = `${area.id}_${view.frontend_view}`;
              permissions[view.frontend_view] = {};

              perms.forEach((perm) => {
                // Usar valor del backend si existe, sino false
                permissions[view.frontend_view][perm] = permissionMap[key] 
                  ? permissionMap[key][perm] || false
                  : false;
              });
            });

            return {
              key: area.id.toString(),
              id: area.id,
              area: area.name,
              permissions,
            };
          });

          setDataSource(combinedDataSource);
        }
      } catch (error) {
        api.error({ message: "Error al cargar datos." });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleFinish = async (values) => {
    setLoading(true);

    const generatePermissionsPayload = () => {
      const permissions = [];
      dataSource.forEach((area) => {
        views.forEach((view) => {
          const perms = area.permissions[view.frontend_view];
          if (!perms) return;

          const permissionObj = {
            view_id: view.id,
            area_id: area.id,
            read: perms["View"] || false,
          };

          // Mapeo consistente según la vista
          if (view.frontend_view === "assignment") {
            permissionObj.create = perms["Assign"] || false;
            permissionObj.update = perms["Unassign"] || false;
            permissionObj.delete = perms["Delete"] || false;
          } else {
            permissionObj.create = perms["Create"] || false;
            permissionObj.update = perms["Update"] || false;
            permissionObj.delete = perms["Delete"] || false;
          }

          if (
            permissionObj.read ||
            permissionObj.create ||
            permissionObj.update ||
            permissionObj.delete
          ) {
            permissions.push(permissionObj);
          }
        });
      });
      return permissions;
    };

    try {
      const payload = {
        ...values,
        permissions: generatePermissionsPayload(),
      };

      if (isEditing) {
        await updateRole(id, payload);
        api.success({ message: "Rol actualizado correctamente" });
      } else {
        await createRole(payload);
        api.success({ message: "Rol creado correctamente" });
      }

      setTimeout(() => {
        navigate("/user-management", { state: { activeTab: "2" } });
      }, 2000);
    } catch (error) {
      api.error({
        message: error.response?.data?.message || "Error al guardar el rol",
      });
    } finally {
      setLoading(false);
    }
  };

  const columns =
    dataSource && selectedView
      ? getColumns(dataSource, setDataSource, selectedView, permissionsView)
      : [];
  const visibleViews = views.filter((view) => view.frontend_view !== "workflows");

  const breadCrumbUrl = [
    { label: "Seguridad", url: "/user-management" },
    { label: isEditing ? (roleData?.name || "Cargando...") : "Nuevo Rol" },
  ];

  const goToPrevious = () => {
    navigate("/user-management", { state: { activeTab: "2" } });
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      requiredPermission="read"
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <Row justify="space-between">
          <Col>
            <PageTitle title={isEditing ? "Editar Rol" : "Agregar nuevo rol"} icon="usermanagement" />
          </Col>
          {
            isEditing &&
            <Col>
              <ChangeLog data={roleData} logs={roleData?.logs || []} />
            </Col>
          }
        </Row>
        
        <Row justify="start" align="middle" gutter={16} style={{ marginBottom: 20 }}>
          <Col style={{ display: "flex", alignItems: "center" }}>
            <BackButton onConfirm={goToPrevious} />
            <BreadCrumb location={breadCrumbUrl} activeKey={1} />
          </Col>
        </Row>

        {loading ? (
          <>
            <Skeleton active paragraph={{ rows: 6 }} />
            <div style={{ height: 32 }} />
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Col>
              <Col xs={24} md={12}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Col>
            </Row>
          </>
        ) : (
          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} md={24}>
              <Card>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  initialValues={{
                    name: "",
                    description: "",
                  }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="name"
                        label="Nombre"
                        rules={[{ required: true, message: "Campo requerido" }]}
                      >
                        <Input maxLength={50} showCount />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="description"
                        label="Descripción"
                        rules={[{ required: true, message: "Campo requerido" }]}
                      >
                        <Input.TextArea
                          maxLength={300}
                          showCount
                          style={{ resize: "none", width: "100%", height: "120px" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Row gutter={16}>
                    <Col span={6}>
                      <Title level={5}>Función</Title>
                      <Menu
                        className="custom-role-menu"
                        mode="inline"
                        selectedKeys={[selectedView]}
                        onClick={({ key }) => setSelectedView(key)}
                        items={visibleViews.map((v) => ({
                          key: v.frontend_view,
                          label: v.name,
                        }))}
                      />
                    </Col>

                    <Col span={18}>
                      <Title level={5}>Permisos</Title>
                      <Table
                        key={`table-${selectedView}`}
                        columns={columns}
                        dataSource={dataSource}
                        bordered
                        pagination={false}
                        scroll={{ x: "max-content", y: 300 }}
                        loading={loading}
                      />
                    </Col>
                  </Row>

                  <Row className="role-view-footer" justify="end">
                    <Col>
                      <Button key="cancel" className="cancel-button" onClick={goToPrevious}>
                        Cancelar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        className="save-type-button"
                        htmlType="submit"
                        icon={<SaveFilled />}
                        size="large"
                        loading={loading}
                      >
                        Guardar
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card>
            </Col>
          </Row>
        )}
      </>
    </WithPermission>
  );
};

export default RoleView;
